import React, { useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Send } from "lucide-react";

/**
 * Compact inline chat for tickets & reports. Reads `messages` array on the parent doc,
 * calls `onSend(body)` when user submits. Disabled when `locked` (e.g. resolved).
 */
export default function InlineChat({ messages = [], onSend, locked = false, currentUserId, testIdPrefix = "chat" }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!body.trim() || busy) return;
    setBusy(true);
    try {
      await onSend(body.trim());
      setBody("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4">
      <div className="max-h-64 overflow-y-auto space-y-2 bg-[#EEF0FA] rounded-md p-3">
        {(!messages || messages.length === 0) && (
          <div className="text-xs text-[#5B6478] text-center py-4">No messages yet — start the conversation.</div>
        )}
        {messages?.map((m) => {
          const mine = m.sender_id === currentUserId;
          const isAdmin = m.sender_role === "admin";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-md px-3 py-2 text-sm ${
                mine ? "bg-[#1E3A8A] text-white" : isAdmin ? "bg-[#7C3AED] text-white" : "bg-white text-[#0F1E4B] border border-[#E4E7F5]"
              }`}>
                <div className="text-[10px] uppercase tracking-widest font-bold opacity-80 mb-0.5">
                  {isAdmin ? "KSB · " : ""}{m.sender_name}
                </div>
                <div>{m.body}</div>
                <div className={`text-[10px] mt-1 ${mine || isAdmin ? "text-white/70" : "text-[#5B6478]"}`}>
                  {new Date(m.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {locked ? (
        <div className="mt-2 text-xs text-[#5B6478] text-center py-2">Conversation closed.</div>
      ) : (
        <div className="mt-2 flex gap-2">
          <Input
            data-testid={`${testIdPrefix}-input`}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="Type a reply..."
            className="border-[#E4E7F5]"
            disabled={busy}
          />
          <Button
            data-testid={`${testIdPrefix}-send`}
            onClick={submit}
            disabled={busy || !body.trim()}
            className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-white"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
